import { useCallback } from 'react';

import useTracking from 'components/Globals/Analytics';
import { COMPONENTS, SECTIONS } from 'components/Globals/Analytics/constants';
import { PORTALS, usePortalActions } from 'components/Globals/PortalsProvider';

import { SUBSCRIPTION_PRODUCT_IDS, VALID_ENTITIES_FOR_SUBSCRIPTION_PRODUCTS } from 'constants/subscriptions';

import { ENTITY_TYPES } from 'operabase-router/constants';

import useAppContext from './useAppContext';
import { useProfileAccesses, useUserData } from './useAuthenticatedUser';
import { useDialogs } from './useDialogs';
import { useStripeCheckout } from './useStripeCheckout';

const usePurchaseSubscription = ({ onChangeProduct } = {}) => {
  const track = useTracking();
  const { paywall } = useAppContext();
  const userData = useUserData();
  const { handleCheckout } = useStripeCheckout();
  const { setPaymentOptionsModal } = useDialogs();
  const { ownerProfile } = useProfileAccesses();
  const pageAccessSwitcherActions = usePortalActions(PORTALS.PAGE_ACCESS_SWITCHER);

  const onPurchaseSubscription = useCallback(
    async ({ productId, entityType, entity }) => {
      if (productId === SUBSCRIPTION_PRODUCT_IDS.ARCHIVE_ACCESS) {
        return handleCheckout({
          userId: ownerProfile?.user?.id || userData?.id,
          entityId: ownerProfile?.profile?.id,
          entityType: ENTITY_TYPES.PROFILE,
          productIds: [productId],
        });
      }

      const hasSelectedEntity = !!entityType && !!entity?.id;
      const isSelectedEntityValidForProduct =
        hasSelectedEntity && VALID_ENTITIES_FOR_SUBSCRIPTION_PRODUCTS[productId]?.includes(entityType);

      track.click({
        section: SECTIONS.PAYWALL,
        component: COMPONENTS.PAYWALL_ENTITY_SELECTOR,
        meta: {
          id: productId,
          actionType: paywall?.paywallType,
          entityType,
          entityId: entity?.id,
        },
      });

      if (isSelectedEntityValidForProduct) {
        if (ENTITY_TYPES.PROFILE === entityType) {
          return handleCheckout({
            userId: userData.id,
            entityId: entity.id,
            entityType,
            productIds: [productId],
          });
        }

        return setPaymentOptionsModal({
          isOpen: true,
          entity,
          entityType,
          preSelectedProduct: productId,
        });
      }

      if (productId) {
        return pageAccessSwitcherActions.show({
          forProductId: productId,
          onSelect: selectedEntityInfo => onPurchaseSubscription({ productId, ...selectedEntityInfo }),
        });
      }

      if (hasSelectedEntity && onChangeProduct) {
        return onChangeProduct({
          entityType,
          entity,
          subscription: entity.subscription,
        });
      }

      return null;
    },
    [
      paywall?.paywallType,
      track,
      setPaymentOptionsModal,
      handleCheckout,
      userData.id,
      ownerProfile?.user?.id,
      ownerProfile?.profile?.id,
      onChangeProduct,
      pageAccessSwitcherActions,
    ],
  );

  return onPurchaseSubscription;
};

export default usePurchaseSubscription;
